/**
 * Type-safe error message extraction
 * Handles unknown error types properly for TypeScript strict mode
 */
export function getErrorMessage(error: unknown, fallback = 'Ein unbekannter Fehler ist aufgetreten'): string {
  // Handle null/undefined
  if (error === null || error === undefined) {
    return fallback;
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error.message || fallback;
  }
  
  // Handle strings
  if (typeof error === 'string') {
    return error || fallback;
  }
  
  // Handle objects with message property
  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;

    // Check for standard error properties
    if ('message' in errObj && typeof errObj.message === 'string') {
      return errObj.message;
    }

    // Check for axios error response
    if ('response' in errObj && errObj.response) {
      const response = errObj.response as {
        data?: unknown;
        statusText?: string;
        status?: number | string;
      };
      if (response.data) {
        if (typeof response.data === 'string') return response.data;
        const responseData = response.data as { message?: unknown; error?: unknown };
        if (responseData.message) return String(responseData.message);
        if (responseData.error) return String(responseData.error);
      }
      return response.statusText || `HTTP Error ${response.status}`;
    }

    // Check for HTTP error
    if ('status' in errObj || 'statusText' in errObj) {
      const status = errObj.status as number | string | undefined;
      const statusText = errObj.statusText as string | undefined;
      return statusText || (status ? `HTTP Error ${status}` : 'HTTP Error');
    }
    
    // Try to stringify if it's a plain object
    try {
      const str = JSON.stringify(error);
      if (str !== '{}') return str;
    } catch (e) {
      // If stringify fails, continue to default fallback
    }
  }
  
  // Fallback to string representation or default message
  try {
    return String(error);
  } catch (e) {
    return fallback;
  }
}

/**
 * Shorthand for common pattern: errorMsg(err) || 'fallback'
 */
export function errorMsg(error: unknown, fallback?: string): string {
  const message = getErrorMessage(error, fallback || '');
  return message || fallback || 'Ein unbekannter Fehler ist aufgetreten';
}
